import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Delete, ModeEdit } from '@mui/icons-material';
import { Box, CircularProgress, Divider, SpeedDialAction } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { ConfirmModal, InfoItem, ViewPage } from '/src/components';
import { usePaymentStore } from '/src/context';
import { toastMessages } from '/src/lib/messages';
import { Note } from '/src/lib/models';
import { CRUDMode, DataModelType } from '/src/lib/types';
import { theme } from '/src/theme';

/**
 * View Payment Page
 */
export const ViewPaymentPage: React.FC = observer(() => {
  /** Context **/
  const paymentStore = usePaymentStore();
  const params = useParams();
  const navigate = useNavigate();

  /** State **/
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);

  /**
   * Array of booleans indicating which sidebar items are open.
   *
   * @remarks
   * Each item's index in the array corresponds to its position in the sidebar.
   */
  const [sidebarItems, setSidebarItems] = useState<boolean[]>([
    false, // Notes
    false, // Documents
  ]);

  /** Computed **/
  const payment = paymentStore.currentPayment;

  /** Methods **/

  /**
   * Removes the Payment.
   *
   * @remarks
   * Redirects to the Payments List afterwards.
   */
  const remove = async () => {
    if (!payment) return;

    try {
      await paymentStore.remove(payment._id);

      toast.success(toastMessages.deletePayment.success);
      navigate('/payments');
    } catch (err) {
      console.error(err);
      toast.error(toastMessages.deletePayment.error);
    }
  };

  /**
   * Updates the Notes list.
   */
  const updateNotes = async (notes: Note[]) => {
    if (!payment) return;

    const response = await paymentStore.update({ ...payment, notes });
    if (!response) throw new Error(`Error updating notes for Payment ID ${payment._id}`);

    paymentStore.currentPayment = await paymentStore.get(payment._id);
  };

  /**
   * Updates the Documents list.
   *
   * @param files List of Document URLs.
   */
  const updateFiles = async (files: string[]) => {
    if (!payment) return;

    const response = await paymentStore.update({ ...payment, files });
    if (!response) throw new Error(`Error updating files for Payment ID ${payment._id}`);

    paymentStore.currentPayment = await paymentStore.get(payment._id);
  };

  /** Effects **/

  useEffect(() => {
    params.paymentId && paymentStore.get(params.paymentId);
  }, [params.paymentId]);

  /** Render **/
  return (
    <ViewPage
      modelName={payment?.name ?? ''}
      modelType={DataModelType.Payment}
      sidebarItems={sidebarItems}
      setSidebarItems={setSidebarItems}
      sidebarFlex="35"
      contentFlex="65"
      noteOptions={{
        mode: CRUDMode.View,
        notes: payment?.notes ?? [],
        onChange: updateNotes,
      }}
      documentOptions={{
        mode: CRUDMode.Edit,
        files: payment?.files ?? [],
        onChange: updateFiles,
      }}
      speedDialActions={[
        <SpeedDialAction
          key="edit-button"
          className="md-fab-action-item"
          icon={<ModeEdit className="material-icons" />}
          slotProps={{
            fab: {
              'aria-label': 'Edit Payment',
              className: 'md-fab md-mini md-accent md-button md-default-theme',
            },
            tooltip: {
              title: 'Edit Payment',
            },
          }}
          onClick={() => navigate(`/payments/${payment?._id}/edit`)}
        />,
        <SpeedDialAction
          key="delete-button"
          className="md-fab-action-item md-warn"
          icon={<Delete className="material-icons" />}
          slotProps={{
            fab: {
              'aria-label': 'Delete Payment',
              className: 'md-fab md-mini md-warn md-button md-default-theme',
              style: { background: theme.palette.warning.main, color: 'white' },
            },
            tooltip: {
              title: 'Delete Payment',
            },
          }}
          onClick={() => setShowConfirmDelete(true)}
        />,
      ]}
      modals={
        <ConfirmModal
          content="Are you sure you want to delete this Payment?"
          open={showConfirmDelete}
          setOpen={setShowConfirmDelete}
          onOk={() => remove()}
          label="Confirm Payment Deletion"
          okLabel="Delete"
          cancelLabel="Cancel"
        />
      }
    >
      {payment && (
        <>
          <InfoItem title="Asset" value={payment?.asset.name} url={`/assets/${payment?.asset._id}`} />
          <InfoItem title="Program" value={payment?.program} />
          <Divider />
          <InfoItem title="Amount" bold value={payment?.amount} currency />
          <Divider />
          <InfoItem title="Payment Type" value={payment?.paymentType} />
          <InfoItem title="Date Paid" value={payment?.datePaid} date />
          <InfoItem title="Date Deposited" value={payment?.dateDeposited} date />
        </>
      )}

      {!payment && (
        <Box display="flex" flex="100" justifyContent="center" alignItems="center" className="pad-top-bottom">
          <CircularProgress className="md-primary" md-mode="indeterminate" />
        </Box>
      )}
    </ViewPage>
  );
});
